/**
 * GA4 Event Validation & Sanitization
 * Ensures all events meet GA4 requirements before sending
 */
import { AnalyticsEvent, GA4Item } from './types'

/**
 * Validate GA4 Item structure
 */
function validateItem(item: GA4Item): boolean {
  if (!item.item_id || typeof item.item_id !== 'string') {
    console.error('[Analytics] Invalid item_id:', item)
    return false
  }
  if (!item.item_name || typeof item.item_name !== 'string') {
    console.error('[Analytics] Invalid item_name:', item)
    return false
  }
  if (typeof item.price !== 'number' || item.price < 0) {
    console.error('[Analytics] Invalid price:', item)
    return false
  }
  if (typeof item.quantity !== 'number' || item.quantity < 1) {
    console.error('[Analytics] Invalid quantity:', item)
    return false
  }
  return true
}

/**
 * Validate event structure
 */
export function validateEvent(event: AnalyticsEvent): boolean {
  // Check event name
  if (!event.event || typeof event.event !== 'string') {
    console.error('[Analytics] Invalid event name:', event)
    return false
  }

  // Validate items array for e-commerce events
  if ('items' in event && Array.isArray(event.items)) {
    if (event.items.length === 0) {
      console.error('[Analytics] Empty items array:', event)
      return false
    }
    if (!event.items.every(validateItem)) {
      return false
    }
  }

  // Validate currency
  if ('currency' in event && event.currency !== 'SAR') {
    console.error('[Analytics] Invalid currency (must be SAR):', event)
    return false
  }

  // Validate value
  if (
    'value' in event &&
    (typeof event.value !== 'number' || event.value < 0)
  ) {
    console.error('[Analytics] Invalid value:', event)
    return false
  }

  return true
}

/**
 * Sanitize event data
 */
export function sanitizeEvent<T extends AnalyticsEvent>(event: T): T {
  const sanitized = { ...event }

  // Truncate long strings
  if ('item_name' in sanitized && typeof sanitized.item_name === 'string') {
    sanitized.item_name = sanitized.item_name.substring(0, 100)
  }

  // Round numbers to 2 decimal places
  if ('value' in sanitized && typeof sanitized.value === 'number') {
    sanitized.value = Math.round(sanitized.value * 100) / 100
  }
  if ('price' in sanitized && typeof sanitized.price === 'number') {
    sanitized.price = Math.round(sanitized.price * 100) / 100
  }

  // Sanitize items
  if ('items' in sanitized && Array.isArray(sanitized.items)) {
    sanitized.items = sanitized.items.map((item) => ({
      ...item,
      item_name: item.item_name.substring(0, 100),
      price: Math.round(item.price * 100) / 100,
    }))
  }

  return sanitized
}
